import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { addFeature, editFeature, getSingleFeature } from "../../redux/services/featureSlice";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { ROUTES } from "../../constant";

const FeatureForm = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const singleFeature = useSelector((state) => state.feature.singleFeatureData);

  const queryParams = new URLSearchParams(location.search);
  const featureId = queryParams.get("featureId");

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const initialValues = {
    title: "",
    thumbnailImage: "",
    description: "",
    link: "",
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    thumbnailImage: Yup.string().required("Thumbnail Image is required"),
    description: Yup.string().required("Description is required"),
    link: Yup.string().required("Link is required"),
  });

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      if (featureId) {
        values.featureId = featureId;
      }

      let response;
      if (featureId) {
        response = await dispatch(editFeature(values));
      } else {
        response = await dispatch(addFeature(values));
      }

      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, {
          autoClose: 2000,
          pauseOnHover: false,
        });
        navigate(ROUTES.WEBSITE.FEATURES);
        resetForm();
      }
    } catch (error) {
      console.error("Error in saving feature:", error);
    } finally {
      setLoading(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setValues, resetForm } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  // Fetch single feature
  const getSingleRecord = useCallback(async () => {
    try {
      await dispatch(getSingleFeature(featureId));
    } catch (error) {
      console.error("Error fetching single feature:", error);
    }
  }, [dispatch, featureId]);

  useEffect(() => {
    if (featureId) {
      getSingleRecord();
    }
  }, [featureId, getSingleRecord]);

  useEffect(() => {
    if (featureId && singleFeature) {
      setValues({
        title: singleFeature?.title ?? "",
        thumbnailImage: singleFeature?.thumbnailImage ?? "",
        description: singleFeature?.description ?? "",
        link: singleFeature?.link ?? "",
      });
      setPreview(singleFeature?.thumbnailImage ?? null);
    }
  }, [featureId, singleFeature, setValues]);

  // Handle image upload (same as HowItWorkForm)
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const imageURL = URL.createObjectURL(file);
    setPreview(imageURL);

    const formData = new FormData();
    formData.append("image", file);

    try {
      setLoading(true);

      const response = await dispatch(fileUpload(formData));

      if (response) {
        const url = response.payload?.fileUrl || response.payload;
        setValues({ ...values, thumbnailImage: url });
        setLoading(false);

        toast.success("Image uploaded successfully!");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Image upload failed");
    }
  };

  return (
    <div>
      <div className="bg-background py-4 sm:py-4">
        <div className="px-4 md:py-10 py-5 bg-card shadow rounded-3xl sm:p-10">
          <p className="md:text-3xl text-xl text-center font-medium uppercase text-primary">{action} Feature</p>

          <form className="mt-6" onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
              {/* Title */}
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-foreground">Title</label>
                <input type="text" name="title" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.title} onChange={handleChange} onBlur={handleBlur} placeholder="Title" />
                {touched.title && errors.title && <div className="text-red-500 text-sm">{errors.title}</div>}
              </div>

              {/* Thumbnail Image Upload */}
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-foreground">Thumbnail Image</label>
                <input type="file" name="thumbnailImage" onChange={handleImageChange} className="w-full px-4 py-2 border border-border rounded-xl" />
                {touched.thumbnailImage && errors.thumbnailImage && <div className="text-red-500 text-sm">{errors.thumbnailImage}</div>}
                {preview && (
                  <div className="mt-4 flex items-center gap-3">
                    <p className="text-md text-foreground">Preview:</p>
                    <img src={preview} alt="preview" className="w-20 h-20 object-cover border border-border rounded-xl" />
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-foreground">Description</label>
                <textarea name="description" rows={5} className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.description} onChange={handleChange} onBlur={handleBlur} placeholder="Description" />
                {touched.description && errors.description && <div className="text-red-500 text-sm">{errors.description}</div>}
              </div>

              {/* Link */}
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-foreground">Link</label>
                <input type="text" name="link" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.link} onChange={handleChange} onBlur={handleBlur} placeholder="Link" />
                {touched.link && errors.link && <div className="text-red-500 text-sm">{errors.link}</div>}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-center">
              <button type="submit" className="w-full sm:w-[250px] min-h-[50px] flex justify-center items-center text-white bg-primary text-lg font-semibold rounded-xl hover:bg-accent mt-7">
                {loading ? <div className="loader"></div> : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeatureForm;
